import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import VerticalLayout from "../../../../../lib/html5-software-library/src/ranj/components/layouts/VerticalLayout";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import MediatorManager from "../../../core/application/MediatorManager";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import SkinFactory from "../../../core/ui/SkinFactory";
import EmailInteractionEvent from "../controller/EmailInteractionEvent";
import EmailDraftItem from "../model/EmailDraftItem";
import EmailModel from "../model/EmailModel";
import EmailConversationViewConstants from "./EmailConversationViewConstants";
import ScrollPane from "/lib/html5-software-library/src/ranj/components/scrolling/ScrollPane";
import Rectangle from "/lib/html5-software-library/src/ranj/geom/Rectangle";
import { isNull } from "/lib/html5-software-library/src/ranj/util/TypeUtil";

export default class EmailConversationMediator extends AbstractMediator {
    /**
     * 
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {RevisableLanguagePack} languagePack 
     * @param {SoundManager} soundManager 
     * @param {EmailModel} emailModel 
     * @param {MediatorManager} mediatorManager 
     */
    constructor(target, dispatcher, languagePack, soundManager, emailModel, mediatorManager) {
        super(target, dispatcher);

        this._languagePack = languagePack;
        this._soundManager = soundManager;
        this._emailModel = emailModel;
        this._mediatorManager = mediatorManager;

        this._conversation = this.getCustomData().conversation;
        this._skinKeyRegistry = this.getCustomData().skinKeyRegistry;

        this._skins = [];
        this._mediators = [];
    }

    initialize() {
        super.initialize();

        if (this._target.emptyIndicationTextField) {
            this._target.emptyIndicationTextField.visible = false;
        }

        if (this._conversation) {
            this.setConversation(this._conversation);
        }

        this.registerEventListener(EmailInteractionEvent.DRAFT_CHOICE_PICKED, this._handleDraftChoicePicked.bind(this));
    }

    destroy() {
        this.cleanup();

        this._conversation = null;

        super.destroy();
    }

    getConversation() {
        return this._conversation;
    }

    setConversation(value) {
        this._conversation = value;

        this.cleanup();
        this.populate();
        this._emailModel.markRead(this._conversation);
    }

    cleanup() {
        for (let i = 0; i < this._mediators.length; i++) {
            this._contentContainer.removeChild(this._mediators[i]._target);
            this._mediators[i].destroy();
        }
        this._mediators = [];

        if (!isNull(this._scrollPane)) {
            this._scrollPane.setScrollPosition(0, 0);
            this._scrollPane.destroy();
            this._scrollPane = null;
        }
    }

    populate() {
        const scskin = this._scrollComponentSkin = this._target;
        const halvedDragButtonHeight = this._scrollComponentSkin.vScrollBar.dragButton.nominalBounds.height * .5;
        this._scrollPane = new ScrollPane(new Rectangle(0, 0, scskin.nominalBounds.width, scskin.nominalBounds.height), null,
            scskin.vScrollBar, scskin.contentContainer, scskin.contentMask, null, null, halvedDragButtonHeight);

        this._contentContainer = this._target.contentContainer;

        // We draw a shape behind the text to make dragging easier for the player
        if (!this._scrollDragHitArea) {
            this._scrollDragHitArea = new createjs.Shape();
            this._scrollDragHitArea.x = 0;
            this._scrollDragHitArea.y = 0;
            this._scrollDragHitArea.alpha = .01;
            this._scrollComponentSkin.contentContainer.addChild(this._scrollDragHitArea);
        }

        let verticalLayout = new VerticalLayout(this._contentContainer, EmailConversationViewConstants.ITEM_SPACING);
        let items = this._conversation.getItems();
        let itemSkinToScrollTo;
        let firstUnreadFound = false;

        for (let i = 0; i < items.length; i++) {
            let item = items[i];
            let itemSkin = (item instanceof EmailDraftItem) ? SkinFactory.getSkin(this._skinKeyRegistry.EMAIL_DRAFT_ITEM_SKIN) : SkinFactory.getSkin(this._skinKeyRegistry.EMAIL_RECEIVED_ITEM_SKIN);
            itemSkin.customData = {
                item,
                skinKeyRegistry: this._skinKeyRegistry
            };

            let itemMediator = this._mediatorManager.createMediator(itemSkin, itemSkin.skinType);
            this._mediators.add(itemMediator);

            verticalLayout.addItem(itemSkin);

            // Remember to scroll to to first unread mail (if any)
            if (!firstUnreadFound) {
                itemSkinToScrollTo = itemSkin;
                if (!item.isRead) {
                    firstUnreadFound = true;
                }
            }
        }

        let scrollPostionY = 0;
        if (itemSkinToScrollTo) {
            scrollPostionY = itemSkinToScrollTo.y;
        }

        let contentHeight = this._contentContainer.getBounds().height + EmailConversationViewConstants.ITEM_SPACING;
        this._scrollPane.setHeightBound(contentHeight);

        this._scrollDragHitArea.graphics.clear();
        const bounds = this._scrollPane.determineBounds();
        this._scrollDragHitArea.graphics.beginFill("#000000");
        this._scrollDragHitArea.graphics.drawRect(bounds.x, bounds.y, bounds.width, bounds.height);

        this._scrollPane.setScrollPosition(0, -scrollPostionY);
    }

    _handleDraftChoicePicked() {
        this.cleanup();
        this.populate();
    }
}
